import { createContext, useContext, useEffect, useState } from "react";
import { ThemeData, loadSavedTheme, saveTheme, resetTheme, getCurrentTheme, loadThemeOnLogin } from "../utlis/themeUtils";

interface User {
  name?: string;
  email?: string;
  permissions?: string;
  attributes?: Record<string, any>;
  [key: string]: any;
}

interface UserContextType {
  permissions: string | null;
  user: User | null;
  theme: ThemeData | null;
  updateTheme: (themeData: ThemeData) => Promise<void>;
  resetUserTheme: () => Promise<void>;
  loading: boolean;
  reloadTheme: () => Promise<void>;
}

const UserContext = createContext<UserContextType>({
  permissions: null,
  user: null,
  theme: null,
  updateTheme: async () => { },
  resetUserTheme: async () => { },
  loading: true,
  reloadTheme: async () => { }
});

//---------------------------------------------------------------------------------------------------------------------

export function UserProvider({ children }: any) {
  const [user, setUser] = useState<User | null>(null);
  const [theme, setTheme] = useState<ThemeData | null>(null);
  const [loading, setLoading] = useState(true);

  // Function to load theme from user profile or localStorage
  const loadTheme = async () => {
    try {
      await loadSavedTheme();

      // Get current theme from CSS variables
      const currentTheme = getCurrentTheme();
      setTheme(currentTheme);
    } catch (error) {
      console.error('Error loading theme:', error);
      // Set default theme if loading fails
      const defaultTheme: ThemeData = {
        primary: '#1D2B56',
        primaryLight: '#2a3a6a',
        primaryDark: '#162142',
        isCustom: false,
        selectedScheme: 'default'
      };
      setTheme(defaultTheme);
    }
  };

  // Function to reload theme (useful after login)
  const reloadTheme = async () => {
    await loadTheme();
  };

  useEffect(() => {
    const initializeUserAndTheme = async () => {
      try {
        // Load user from localStorage
        const userData = JSON.parse(localStorage.getItem("user") || "{}");
        setUser(userData);

        // Load theme
        await loadTheme();
      } catch (error) {
        console.error('Error initializing user and theme:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeUserAndTheme();
  }, []);

  // Listen for authentication changes and reload theme
  useEffect(() => {
    const checkAuthAndReloadTheme = async () => {
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";
      if (isAuthenticated && currentUser.id && (!user || user.id !== currentUser.id)) {
        setUser(currentUser);
        // Load theme from user attributes on login
        await loadThemeOnLogin();
        await loadTheme(); // Reload theme when user changes
      }
    };

    // Check immediately
    checkAuthAndReloadTheme();

    // Listen for storage changes (login/logout)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "user" || e.key === "isAuthenticated") {
        checkAuthAndReloadTheme();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom login event
    const handleLogin = async () => {
      setTimeout(async () => {
        await checkAuthAndReloadTheme();
      }, 100); // Small delay to ensure user data is saved
    };

    window.addEventListener('userLogin', handleLogin);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLogin', handleLogin);
    };
  }, [user]);

  const updateTheme = async (themeData: ThemeData) => {
    try {
      await saveTheme(themeData);
      setTheme(themeData);
    } catch (error) {
      console.error('Error updating theme:', error);
    }
  };

  const resetUserTheme = async () => {
    try {
      await resetTheme();
      const defaultTheme: ThemeData = {
        primary: '#1D2B56',
        primaryLight: '#2a3a6a',
        primaryDark: '#162142',
        isCustom: false,
        selectedScheme: 'default'
      };
      setTheme(defaultTheme);
    } catch (error) {
      console.error('Error resetting theme:', error);
    }
  };

  const permissions = user?.permissions || null;

  return (
    <UserContext.Provider value={{
      permissions,
      user,
      theme,
      updateTheme,
      resetUserTheme,
      loading,
      reloadTheme
    }}>
      {children}
    </UserContext.Provider>
  );
}

export const useUser = () => {
  return useContext(UserContext)
}